import type { Store } from "@/data/repositories";
import type { Clock } from "@/domain/clock";
import { newId } from "@/domain/ids";
import { detectLocale } from "@/app/i18n/detect";
import type { Api, EventsPayload } from "./ApiClient";

export type EventProps = Record<string, string | number | boolean>;

export interface Environment {
  userAgent: string;
  webdriver: boolean;
  standalone: boolean;
  language: string | undefined;
}

export interface AnalyticsTimers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export const FLUSH_DELAY_MS = 10_000;
export const SESSION_GAP_MS = 30 * 60 * 1000;

/**
 * Anonymous usage counters. Events queue up and leave in batches: after a short
 * delay, or at once with a beacon when the page goes to the background. The
 * device id is random and lives in settings; nothing about the person is sent.
 */
export class AnalyticsService {
  private queue: EventsPayload["events"] = [];
  private timer: unknown = null;
  private lastSessionStart = 0;
  private deviceId: string | null = null;
  private readonly timers: AnalyticsTimers;

  constructor(
    private readonly api: Api,
    private readonly store: Store,
    private readonly clock: Clock,
    private readonly env: Environment,
    timers?: AnalyticsTimers,
  ) {
    this.timers = timers ?? { setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };
  }

  /** Records that the app is open; called at start and whenever it comes back after a long pause. */
  async sessionStart(): Promise<void> {
    const now = this.clock.now().getTime();
    if (now - this.lastSessionStart < SESSION_GAP_MS) return;
    this.lastSessionStart = now;
    const settings = await this.store.settings.get();
    this.track("session_start", {
      locale: settings.locale ?? detectLocale(this.env.language),
      localeSource: settings.locale ? "manual" : "auto",
      platform: platformOf(this.env.userAgent),
      standalone: this.env.standalone,
    });
  }

  track(name: string, props: EventProps = {}): void {
    this.queue.push({ name, ts: this.clock.now().toISOString(), props });
    if (this.timer === null) this.timer = this.timers.setTimeout(() => void this.flush(false), FLUSH_DELAY_MS);
  }

  /** Sends what is queued; with `beacon` the request survives the page closing. */
  async flush(beacon: boolean): Promise<void> {
    if (this.timer !== null) {
      this.timers.clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, 200);
    try {
      const settings = await this.store.settings.get();
      const deviceId = await this.ensureDeviceId();
      await this.api.sendEvents({ deviceId, platform: platformOf(this.env.userAgent), webdriver: this.env.webdriver, events }, settings.session?.token, beacon);
    } catch {
      // Statistics never get in the way; a lost batch is a lost batch.
    }
  }

  get pending(): ReadonlyArray<EventsPayload["events"][number]> {
    return this.queue;
  }

  private async ensureDeviceId(): Promise<string> {
    if (this.deviceId) return this.deviceId;
    const settings = await this.store.settings.get();
    if (settings.deviceId) return (this.deviceId = settings.deviceId);
    const id = newId();
    await this.store.settings.update({ deviceId: id });
    return (this.deviceId = id);
  }
}

export function platformOf(userAgent: string): string {
  if (/iPhone|iPad|iPod/.test(userAgent)) return "ios";
  if (/Android/.test(userAgent)) return "android";
  return "desktop";
}

export function browserEnvironment(): Environment {
  return {
    userAgent: navigator.userAgent,
    webdriver: navigator.webdriver === true,
    standalone: window.matchMedia?.("(display-mode: standalone)").matches === true || (navigator as { standalone?: boolean }).standalone === true,
    language: navigator.language,
  };
}
